<template>
  <div
    :class="{
      'search-fact-finder-fi': true,
      'search-fact-finder-fi--website': isWebsiteSearch,
      'search-fact-finder-fi--fite': isFite,
    }"
  >
    <div
      v-if="!searchFactFinderNg.initializing || skipSearch"
      class="search-fact-finder-fi__results"
      :class="headerHeightClasses"
    >
      <slot
        :search-fact-finder-ng="searchFactFinderNg"
        :skip-search="skipSearch"
        name="heading"
      />

      <div
        :class="{
          'fi-shadow-lg': filtersOutOfView && !isFite && isSM,
          'search-fact-finder-fi__results__filter-container': !isFite && isSM,
        }"
      >
        <div
          v-if="filtersOutOfView && !isFite"
          class="search-fact-finder-fi__expander"
          :class="{ 'fi-shadow-lg': !showFilters }"
          @click="toggleFilter"
        >
          <icon-fi
            icon="filter"
            class="search-fact-finder-fi__expander-filter-icon"
          />
          <div class="search-fact-finder-fi__expander-filter-text">
            {{ $dict.get('search.Fact-Finder.Search-Result.Filter') }}
          </div>
          <icon-fi
            class="search-fact-finder-fi__expander-filter-arrow"
            :icon="showFilters ? 'chevron-up' : 'chevron-down'"
          />
        </div>

        <slide-up-down-fi
          :model-value="showFilters || !filtersOutOfView"
          :duration="400"
        >
          <grid-container-fi>
            <div
              v-if="!isSearchPage && categories.length"
              class="search-fact-finder-fi__results__filter search-fact-finder-fi__results__filter--categories"
            >
              <fact-finder-categories
                :categories="categories"
                :filter-url-params="filterUrlParams"
                @toggle="onToggleCategory"
              />
            </div>
            <div
              v-if="menuFacets.length && !showNoQueryHint && !skipSearch"
              class="search-fact-finder-fi__results__filter search-fact-finder-fi__results__filter--facets"
              :class="{
                'search-fact-finder-fi__results__filter--fixed':
                  filtersOutOfView && !isFite && !isSM,
              }"
            >
              <facets-menu-fi
                :facets="menuFacets"
                :allow-clear-all="!autoFilters"
                :filters-out-of-view="filtersOutOfView"
                @change="changeFacet"
                @clear-all-facets="clearAllFacets"
                @mobile-facet-closed="mobileFacetClosed"
              >
                <template
                  #facets="{ facet, open, change, close, toggleOpen, idx, registerDomElement }"
                >
                  <fact-finder-facet-fi
                    :facet="facet"
                    :open="open"
                    :idx="idx"
                    :register-dom-element="registerDomElement"
                    @change="change"
                    @toggle-open="onToggleOpen($event, toggleOpen, facet)"
                    @close="close(facet)"
                  />
                </template>
                <template #selection="{ facet, element }">
                  {{ cleanName(facet.name) }}:
                  <span
                    v-if="facet.type === 'FLOAT' || facet.type === 'INTEGER'"
                    class="facets-selection-fi__value"
                  >
                    {{ getLocalizedValue(element.selectedMinValue) }}
                    [{{ facet.unit }}] -
                    {{ getLocalizedValue(element.selectedMaxValue) }}
                    [{{ facet.unit }}]
                  </span>
                  <span
                    v-else
                    class="facets-selection-fi__value"
                  >
                    {{ element.text }}
                  </span>
                </template>
              </facets-menu-fi>
            </div>
          </grid-container-fi>
        </slide-up-down-fi>
      </div>
      <div
        v-if="!isFite"
        v-intersect="onIntersect"
      />
      <grid-container-fi :container="(searchFactFinderNg?.totalHits || 0) === 0 || skipSearch">
        <div
          v-if="showNoQueryHint"
          class="search-fact-finder-fi__show-no-query-hint"
          :class="{ 'search-fact-finder-fi__show-no-query-hint-no-filters': !noFilters }"
        >
          <grid-container-fi>
            <div class="search-fact-finder-fi__query">
              {{ $dict.get('search.Fact-Finder.No-Search-Result.Heading') }}
            </div>
          </grid-container-fi>

          <fact-finder-no-results-fi
            :query="searchFactFinderNg.query"
            :fields="fields"
            :no-filters="noFilters"
            :show-result-suggestion="true"
            :is-category="isCategory"
            @new-search="newSearch"
          />
        </div>
        <div
          v-else-if="(searchFactFinderNg?.totalHits || 0) === 0 || skipSearch"
          :class="{
            'fi-col-start-1': true,
            'fi-col-end-13': true,
            'sm:fi-col-start-3': true,
            'sm:fi-col-end-11': true,
            'lg:fi-col-start-3': noFilters,
            'lg:fi-col-end-11': noFilters,
            'lg:fi-col-start-5': !noFilters,
            'lg:fi-col-end-9': !noFilters,
          }"
        >
          <fact-finder-no-results-fi
            :query="searchFactFinderNg.query"
            :fields="fields"
            :no-filters="noFilters"
            :is-category="isCategory"
            @new-search="newSearch"
          />
        </div>
        <div
          v-else
          class="fi-col-span-full"
        >
          <div
            ref="itemWrapper"
            class="search-fact-finder-fi__item-wrapper"
          >
            <loading-indicator
              v-if="isCategory && $apollo.loading"
              variant="spinnerLocal"
            />

            <div class="search-fact-finder-fi__item-wrapper-inner">
              <div
                class="search-fact-finder-fi__count"
                data-test="fact-finder-count"
                :data-test-2="familiesCount ? familiesCount : null"
                :data-test-3="productsCount ? productsCount : null"
              >
                <span v-if="familiesCount && productsCount">
                  {{
                    $dict.get('search.Families-And-Variants-Count', [familiesCount, productsCount])
                  }}
                </span>
                <span v-else-if="familiesCount">
                  {{ $dict.get('search.Families-Count', [familiesCount]) }}
                </span>
                <span v-else-if="productsCount">
                  {{ $dict.get('search.Variants-Count', [productsCount]) }}
                </span>
              </div>

              <div class="search-fact-finder-fi__items">
                <template
                  v-for="(record, recordIdx) in searchFactFinderNg?.masters?.hits
                    ? searchFactFinderNg.masters.hits
                    : []"
                  :key="record?.idString"
                >
                  <fact-finder-item-fi
                    v-if="record"
                    :record="record"
                    :record-idx="recordIdx"
                    :filter-url="filterUrlParamsNoQuery"
                    :show-description="includeClaim"
                    type="family"
                    @click="masterClicked(record)"
                  />
                </template>

                <fact-finder-item-fi
                  v-for="(record, recordIdx) in variantHits"
                  :key="record.variantValuesObject[0].ID"
                  :record="record"
                  :record-idx="recordIdx + (searchFactFinderNg.masters?.hits?.length || 0)"
                  :best-price="getBestPrice(record)"
                  :filter-url="filterUrlParamsNoQuery"
                  :show-description="includeClaim"
                  :type="isWebsiteSearch ? 'page' : 'product'"
                />

                <template v-if="!isCategory">
                  <infinite-loading
                    v-if="infiniteLoadingAvailable && !IS_SSR"
                    ref="infiniteLoading"
                    class="search-fact-finder-fi__spinner"
                    @infinite="fetchMore"
                  >
                    <template #spinner>
                      <div class="search-fact-finder-fi__spinner-tile">
                        <loading-indicator variant="spinnerLocal" />
                      </div>
                    </template>
                    <template #no-more><span /></template>
                    <template #no-results><span /></template>
                    <template #error><span /></template>
                  </infinite-loading>

                  <div
                    v-for="(item, idx) in placeholders"
                    :key="idx"
                    class="search-fact-finder-fi__spinner"
                  >
                    <div class="search-fact-finder-fi__spinner-tile" />
                  </div>
                </template>
              </div>

              <div
                v-if="!isCategory"
                class="search-fact-finder-fi__pagination"
              >
                <a
                  v-if="previousPageUrlParam"
                  v-show="false"
                  :href="previousPageUrlParam"
                >
                  {{ $dict.get('search.Previous-Results') }}
                </a>
                <pagination-progress-bar
                  v-if="searchFactFinderNg?.totalHits"
                  :pagination-page="page"
                  :pagination-total="totalItems || 0"
                  :progressbar-value="pagingPercentage"
                  :disabled-state="Boolean($apolloData.loading)"
                  :show-more-button="displayedItems < totalItems"
                  :query-params="filterUrlParams"
                  @loadmore="fetchMore"
                >
                  <template v-if="page * pageSize <= totalItems">
                    <span>{{ getPaginationString([page * pageSize, totalItems]) }}</span>
                  </template>

                  <template v-else>
                    <span>{{ getPaginationString([totalItems, totalItems]) }}</span>
                  </template>
                </pagination-progress-bar>
              </div>
            </div>
          </div>
        </div>
      </grid-container-fi>
    </div>
    <!-- slot: -->
    <slot
      name="withResults"
      :search-fact-finder-ng="searchFactFinderNg"
    />
    <!-- /slot -->
  </div>
</template>

<script>
import { IconFi, LoadingIndicator, PaginationProgressBar, SlideUpDownFi } from 'atoms';
import InfiniteLoading from 'infinite-loading-vue3-ts';
import { mapGetters } from 'vuex';

import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import factFinderNgNameCleaner from '@/components/search-fact-finder-fi/factFinderNgNameCleaner';
import { useHeaderHeight } from '@/composables/headerHeight';
import { useTracking } from '@/composables/tracking';
import GET_SHOPIFY_PRODUCTS from '@/graphql/getShopifyProducts.gql';
import SEARCH_FACT_FINDER_NG from '@/graphql/searchFactFinderNg.gql';
import SEARCH_FACT_FINDER_NG_WEBSITE from '@/graphql/searchFactFinderNgWebsite.gql';
import { categoryFilterExpander } from '@/lib/fischerPlugin';
import getEventHubSessionId from '@/lib/getEventHubSessionId';
import breakpointMixin from '@/mixins/breakpointMixin.vue';
import eventHubMixin from '@/mixins/eventHubMixin.vue';
import localizedValueMixin from '@/mixins/localizedValueMixin.vue';
import multiBestPriceMixin from '@/mixins/multiBestPriceMixin.vue';
import trackingMixin from '@/mixins/trackingMixin.vue';

import autoFiltersMixin from './autoFiltersMixin.vue';
import FactFinderCategories from './fact-finder-categories/FactFinderCategories.vue';
import FactFinderFacetFi from './fact-finder-facet-fi/FactFinderFacetFi.vue';
import FactFinderItemFi from './fact-finder-item-fi/FactFinderItemFi.vue';
import FactFinderNoResultsFi from './fact-finder-no-results-fi/FactFinderNoResultsFi.vue';
import mergeFacets from './mergeFacets';
import stringifyFilterValue from './stringifyFilterValue';
import technicalFacets from './technicalFacets';
import toFilterInput from './toFilterInput';

function getSearchQuery() {
  return this.isWebsiteSearch ? SEARCH_FACT_FINDER_NG_WEBSITE : SEARCH_FACT_FINDER_NG;
}

function updateSearchFactFinderNg(data) {
  return this.isWebsiteSearch ? data.searchFactFinderNgWebsite : data.searchFactFinderNg;
}

export default {
  name: 'SearchFactFinderFi',
  components: {
    FacetsMenuFi,
    FactFinderCategories,
    FactFinderFacetFi,
    FactFinderItemFi,
    FactFinderNoResultsFi,
    GridContainerFi,
    InfiniteLoading,
    LoadingIndicator,
    PaginationProgressBar,
    IconFi,
    SlideUpDownFi,
  },
  mixins: [
    breakpointMixin,
    multiBestPriceMixin,
    autoFiltersMixin,
    trackingMixin,
    eventHubMixin,
    localizedValueMixin,
  ],
  provide() {
    return {
      getSearchData: () => ({
        query: this.queryPrefix ? this.queryPrefix : this.searchFactFinderNg.query,
        pageSize: this.pageSize,
        attachBreadcrumb: this.attachBreadcrumb,
        clickSource: this.clickSource,
      }),
    };
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    categoryPathIdRoot: {
      type: String,
      default: null,
    },
    clickSource: {
      type: String,
      required: true,
    },
    queryPrefix: {
      type: String,
      default: null,
    },
    isSearchPage: {
      type: Boolean,
      default: false,
    },
    isWebsiteSearch: {
      type: Boolean,
    },
    isActive: Boolean,
    attachBreadcrumb: Boolean,
  },
  emits: ['total-hits'],
  setup() {
    const { trackViewItemList, trackSelectItem } = useTracking();
    const { headerHeightClasses } = useHeaderHeight();
    return { trackViewItemList, trackSelectItem, headerHeightClasses };
  },
  data() {
    return {
      searchFactFinderNg: {
        masters: null,
        variants: null,
        facets: null,
        facetDataUnselected: null,
        query: '',
        totalHits: null,
        initializing: true,
      },
      searchFactFinderNgBaseFacets: {
        facets: null,
      },
      filters: [],
      page: 0,
      pageSize: 15,
      loadMorePage: 0,
      categoryOverride: null,
      shopifyProducts: null,
      showFilters: true,
      filtersOutOfView: false,
    };
  },
  computed: {
    ...mapGetters({ suggestionsQuery: 'search/suggestionsQuery' }),
    showNoQueryHint() {
      return this.isSearchPage && this.trimmedQuery === '';
    },
    isSuggestionsOpen() {
      return (
        this.$store.getters['navigation/getShowSearchSuggest'] ||
        this.$store.getters['search/getQueryTrimmed'] !==
          this.$store.getters['search/suggestionsQuery']
      );
    },
    termToTrack() {
      if (!this.isSearchPage || !this.isActive) {
        return null;
      }
      return this.searchFactFinderNg?.query || null;
    },
    productIds() {
      const ids = [];
      if (!this.isWebsiteSearch && this.searchFactFinderNg?.variants?.hits) {
        this.searchFactFinderNg.variants.hits.forEach((variant) => {
          const id = variant.variantValuesObject?.[0].ID;
          if (id) {
            ids.push(id);
          }
        });
      }
      return ids;
    },
    includeClaim() {
      return Boolean(this.fields.showProductDescription?.value);
    },
    isShop() {
      return this.$fischer.shop.isClosedShop() || this.$fischer.shop.isOnlineShop();
    },
    variantHits() {
      if (!this.searchFactFinderNg.variants?.hits) {
        return [];
      }
      return this.searchFactFinderNg.variants.hits;
    },
    typeFilter() {
      if (this.isWebsiteSearch) {
        return 'page';
      }
      return 'product';
    },
    trimmedQuery() {
      if (this.isSearchPage) {
        return this.query.trim() || '';
      }

      return this.query.trim() || '*';
    },
    noFilters() {
      return this.filters.length === 0;
    },
    totalItems() {
      if (this.isCategory) {
        return this.searchFactFinderNg?.masters?.totalHits;
      }
      return this.searchFactFinderNg?.totalHits || 0;
    },
    infiniteLoadingAvailable() {
      return this.totalItems && this.displayedItems < this.totalItems && !this.previousPageUrlParam;
    },
    placeholders() {
      if (!this.infiniteLoadingAvailable) {
        return [];
      }
      let count = this.totalItems - this.displayedItems;
      count = Math.max(0, count);
      count = Math.min(250, count);
      return Array.from('x'.repeat(count));
    },
    query() {
      if (this.categoryPathIdRoot) {
        return '';
      }
      return this.$store.getters['search/getQuery'];
    },
    categories() {
      if (!this.isCategory) {
        return [];
      }
      const categoryFacetElements = this.searchFactFinderNgBaseFacets?.categoryFacets
        ?.filter((facet) => facet.catalogCategoryData?.CategoryRole === 'INTERMEDIATE')
        .sort((a, b) => a.catalogCategoryData.SortOrder - b.catalogCategoryData.SortOrder);
      if (!categoryFacetElements) {
        return [];
      }
      let currentFacets = this.searchFactFinderNg.categoryFacets?.map((facet) => facet.text);
      if (!currentFacets?.length) {
        const firstMaster = this.searchFactFinderNg?.masters?.hits?.[0]?.masterValuesObject;
        let firstHitPath;
        if (firstMaster?.catalogCategoryData) {
          firstHitPath = firstMaster.catalogCategoryData.CategoryDisplayNamePath;
        } else {
          firstHitPath =
            firstMaster?.masterProductData?.catalogProductData?.CategoryDisplayNamePath;
        }
        if (firstHitPath) {
          currentFacets = categoryFacetElements
            .filter((facet) => firstHitPath.includes(facet.catalogCategoryData.CategoryName))
            .map((facet) => facet.text);
        } else {
          currentFacets = [];
          console.error('Could not find category id');
        }
      }
      return categoryFacetElements.map((filter) => {
        const selected = this.categoryOverride === filter.text;
        return {
          id: filter.text,
          name: filter.catalogCategoryData.CategoryName,
          Slug: filter.catalogCategoryData.Slug,
          imageUrl: filter.catalogCategoryData.imageUrl,
          enabled:
            selected ||
            currentFacets.includes(filter.text) ||
            this.searchFactFinderNg.categoryFacetsUnselected?.includes(filter.text),
          selected,
        };
      });
    },
    facets() {
      const mergedFacets = mergeFacets(
        this.searchFactFinderNg.facetDataUnselected || [],
        this.searchFactFinderNg.facets || [],
        this.searchFactFinderNgBaseFacets.facets || [],
        this.autoFilters || this.filters,
      );

      // make it possible to deselect autoFilters, even if no results found
      if (this.autoFilters?.length) {
        this.autoFilters.forEach((autoFilter) => {
          if (
            !mergedFacets.some(
              (facet) => facet.associatedFieldName === autoFilter.associatedFieldName,
            )
          ) {
            mergedFacets.push({
              associatedFieldName: autoFilter.associatedFieldName,
              disabled: false,
              hidden: false,
              type: 'TEXT',
              name: autoFilter.associatedFieldName,
              unit: '',
              elements: [
                {
                  disabled: false,
                  associatedFieldName: autoFilter.associatedFieldName,
                  text: autoFilter.fakeValue,
                  name: autoFilter.name,
                  selected: true,
                  selectedBool: true,
                  absoluteMaxValue: null,
                  absoluteMinValue: null,
                },
              ],
            });
          }
        });
      }

      return mergedFacets;
    },
    menuFacets() {
      return this.facets.map((facet) => ({
        ...facet,
        elements: facet.elements.map((element) => ({
          ...element,
          selected: element.selectedBool,
          name: element.text,
        })),
      }));
    },
    filterUrlParams() {
      const query = this.query.trim();
      if (!query) {
        return this.filterUrlParamsNoQuery;
      }
      const search = this.filterUrlParamsNoQuery ? `${this.filterUrlParamsNoQuery}&` : '?';
      return `${search}query=${encodeURIComponent(query)}`;
    },
    filterUrlParamsNoQuery() {
      if (this.useAutoFilters) {
        return '';
      }
      const search = new URLSearchParams();

      const tab = this.$fischer.query('tab');
      if (tab) {
        search.append('tab', tab);
      }

      const otherFiltersStart = this.isWebsiteSearch ? 'filter' : 'websitefilter';
      Object.entries(this.$fischer.query()).forEach(([key, value]) => {
        if (key.startsWith(otherFiltersStart)) {
          search.append(key, value);
        }
      });

      const prefix = this.isWebsiteSearch ? 'websitefilter' : 'filter';
      this.filters.forEach((filter) => {
        if (!filter.associatedFieldName || filter.associatedFieldName === 'undefined') {
          console.error('filter has no associated field name', filter);
          return;
        }
        search.append(`${prefix}${filter.associatedFieldName}`, stringifyFilterValue(filter));
      });

      const searchString = search.toString();
      if (searchString.length === 0) {
        return '';
      }

      return `?${searchString}`;
    },
    graphQlVariables() {
      let extraFilters = [
        {
          associatedFieldName: 'Type',
          name: this.typeFilter,
        },
      ];

      if (!this.isSearchPage) {
        extraFilters.push({
          associatedFieldName: 'Hidden_K',
          name: '!true',
        });
      }

      return {
        baseSiteId: this.$fischer.baseSiteId(),
        brand: this.$fischer.brandId(),
        language: this.$fischer.catalogLocale(),
        filters: (this.autoFilters || this.filters).map(toFilterInput),
        quantity: this.isCategory ? 100000 : this.pageSize, // set to max value for fact finders 'hitsPerPage' param (100000)
        query: this.trimmedQuery,
        navigation: Boolean(this.categoryPathIdRoot),
        extraFilters,
        categoryId: this.categoryPathIdRoot,
        catalogLocale: this.$fischer.catalogLocale(),
        factFinderChannel: this.$fischer.factFinderChannel(),
        categoryFacets: this.isCategory,
      };
    },
    previousPageUrlParam() {
      const pageParam = this.$fischer.query('page');
      if (!pageParam) {
        return null;
      }
      const page = Number(pageParam);
      if (this.page !== page) {
        return null;
      }
      const previous = page - 1;
      if (!previous) {
        return null;
      }
      if (previous === 1) {
        return this.$fischer.uri();
      }
      return `?page=${previous}`;
    },
    displayedItems() {
      return Math.min(this.page * this.pageSize, this.searchFactFinderNg?.totalHits);
    },
    pagingPercentage() {
      return (100 * this.displayedItems) / this.totalItems;
    },
    familiesCount() {
      return this.searchFactFinderNg?.totalFamilyHits || null;
    },
    productsCount() {
      return this.searchFactFinderNg?.variants?.totalHits || null;
    },
    isCategory() {
      return Boolean(this.$fischer.isCategoryPage());
    },
    variantsWithoutFamily() {
      if (IS_SHOWROOM && this.isFite) {
        return true;
      }
      return Boolean(this.fields.variantsWithoutFamily?.value);
    },
    skipSearch() {
      if (!this.isSearchPage) {
        return false;
      }

      return this.isSuggestionsOpen || this.showNoQueryHint || this.trimmedQuery === '*';
    },
    hasShopifyProducts() {
      return this.searchFactFinderNg?.masters?.hits?.some((hit) =>
        hit?.masterValuesObject?.masterProductData?.catalogProductData?.Attributes?.find(
          (attribute) => attribute.AttributeKey === 'ShopifyID',
        ),
      );
    },
  },
  watch: {
    query() {
      this.changeFacet();
    },
    'searchFactFinderNg.totalHits': function watchTotalHits(value) {
      if (value !== undefined) {
        this.$emit('total-hits', value);
      }
    },
    termToTrack(newValue) {
      if (!newValue) {
        return;
      }
      if (this.lastTracked === newValue) {
        return;
      }
      this.lastTracked = newValue;

      this.sendEvent('search', {
        searchTerm: newValue,
        searchContext: this.isWebsiteSearch ? 'website' : 'product',
      });
    },
  },
  mounted() {
    try {
      const filterExpand = JSON.parse(window.sessionStorage.getItem(categoryFilterExpander));
      if (filterExpand === false) {
        this.showFilters = filterExpand;
      }
    } catch (error) {
      console.error(error);
    }
  },
  created() {
    this.parseUrlParams();
  },
  apollo: {
    searchFactFinderNg: {
      query: getSearchQuery,
      variables() {
        this.parseUrlParams(); // this might have been done in the created hook, however in SSR mode it won't
        const masterFilters =
          this.isWebsiteSearch || this.categoryPathIdRoot || !this.isFite
            ? undefined
            : [
                {
                  associatedFieldName: 'HasFamily',
                  name: 'true',
                },
              ];

        return {
          ...this.graphQlVariables,
          categoryId: this.categoryOverride || this.graphQlVariables.categoryId,
          masterFilters,
          page: this.pageNonResponsive,
          noVariants: Boolean(this.categoryPathIdRoot),
          variantsWithoutFamily: this.variantsWithoutFamily,
          noFacets: this.pageNonResponsive > 1,
          includeClaim: this.includeClaim,
          originalCategoryId: this.graphQlVariables.categoryId,
          sid: this.isCategory ? undefined : getEventHubSessionId(),
        };
      },
      result({ data }) {
        if (this.fetchingMore) {
          const loaded = this.fetchingMore;
          this.fetchingMore = false;
          setTimeout(loaded, 1000); // throttle requests
          return;
        }
        if (!this.$fischer.query('page')) {
          this.page = 1;
          this.pageNonResponsive = 1;
          if (this.$refs.infiniteLoadingProducts?.stateChanger) {
            this.$refs.infiniteLoadingProducts.stateChanger.reset();
          }
        }

        if (data?.searchFactFinderNg?.masters?.hits) {
          const trackingList = data.searchFactFinderNg.masters.hits.map(
            (hit) =>
              hit.masterValuesObject?.catalogCategoryData ||
              hit.masterValuesObject?.masterProductData?.catalogProductData,
          );
          this.trackViewItemList(trackingList);
        }
      },
      update: updateSearchFactFinderNg,
      skip() {
        return this.skipSearch;
      },
    },
    searchFactFinderNgBaseFacets: {
      query: getSearchQuery,
      variables() {
        return {
          ...this.graphQlVariables,
          filters: [],
          query: this.trimmedQuery,
          includeResults: false,
          noSearch: true,
          variantsWithoutFamily: this.variantsWithoutFamily,
          includeUnselectedFacets: false,
          accessToken: null,
        };
      },
      update: updateSearchFactFinderNg,
      skip() {
        return this.isWebsiteSearch || this.skipSearch;
      },
    },
    getShopifyProducts: {
      query: GET_SHOPIFY_PRODUCTS,
      variables() {
        return {
          first: 100,
        };
      },
      result({ data }) {
        if (data?.getShopifyProducts?.products?.edges) {
          this.$store.commit(
            'shopify/SET_SHOPIFY_PRODUCTS',
            data.getShopifyProducts.products.edges,
          );
        }
      },
      loading(loading) {
        this.$store.commit('shopify/SET_LOADING', loading);
      },
      skip() {
        if (!this.isFite || !this.fields?.enableShopify?.value) {
          return true;
        }

        return !this.hasShopifyProducts;
      },
    },
  },
  methods: {
    onToggleCategory(category, selected) {
      this.categoryOverride = selected ? category.id : null;

      if (this.isFite) {
        this.scrollToResults();
      }
    },
    onToggleOpen(_event, toggleOpen, facet) {
      toggleOpen(facet);
    },
    getBestPrice(record) {
      if (!this.isShop) {
        return null;
      }
      const id = record?.variantValuesObject?.[0] && record.variantValuesObject[0].ID;
      const bestPrice = this.bestPrices[id];
      if (bestPrice !== undefined) {
        return bestPrice;
      }
      return {
        price: {
          loading: true,
        },
      };
    },
    cleanName(name) {
      return factFinderNgNameCleaner(name);
    },
    parseUrlParams() {
      if (this.haveParsedUrlParams) {
        return;
      }
      this.haveParsedUrlParams = true;

      const isCorrectTab = this.isWebsiteSearch === (this.$fischer.query('tab') === 'website');

      const pageParam = isCorrectTab ? this.getPageParam() : 1; // remember NOW, since changeFacet will change it

      const technicalFacetsLowerCase = technicalFacets.map((name) => name.toLowerCase());
      const filters = [];
      const search = this.$fischer.query();
      const searchRegExp = this.isWebsiteSearch ? /^websitefilter(.*)/ : /^filter(.*)/;
      Object.keys(search).forEach((key) => {
        const match = searchRegExp.exec(key);
        if (!match) {
          return;
        }

        const [, name] = match;
        const nameLowerCase = name.toLowerCase();
        if (!name || technicalFacetsLowerCase.includes(nameLowerCase)) {
          return;
        }

        if (/[^~]~~[^~]/.test(name)) {
          const values = /^([0-9.]+)-([0-9.]+)/.exec(search[key]);
          if (values) {
            filters.push({
              element: {
                associatedFieldName: name,
                selectedMinValue: values[1],
                selectedMaxValue: values[2],
                selected: true,
              },
              value: true,
            });
            return;
          }
        }
        filters.push({
          element: {
            associatedFieldName: name,
            name: search[key],
            selected: true,
          },
          value: true,
        });
      });

      if (!this.categoryPathIdRoot) {
        const query = this.$fischer.query('query');
        if (query && query !== this.query) {
          this.$store.dispatch('search/setQuery', { value: query, noShowSuggestions: true });
          this.$store.commit('search/SET_SEARCH_SUGGESTIONS', { suggestions: query, query });
        }
      }

      this.changeFacet(...filters);

      this.page = pageParam;
      this.pageNonResponsive = pageParam;
    },
    getPageParam() {
      const queryParam = this.$fischer.query('page');
      return Number(queryParam) || 1;
    },
    newSearch(search) {
      this.clearAllFacets();
      this.$store.dispatch('search/setQuery', search);
    },
    async fetchMore({ complete, loaded } = {}) {
      if (this.fetchingMore) {
        return;
      }
      if (this.page * this.pageSize > (this.searchFactFinderNg?.totalHits || 0)) {
        if (complete) {
          complete();
        }
        return;
      }
      this.fetchingMore = loaded || (() => null);

      this.page += 1;
      const result = await this.$apollo.queries.searchFactFinderNg.fetchMore({
        variables: {
          page: this.page,
          includeUnselectedFacets: false,
        },
      });

      if (result?.data?.searchFactFinderNg?.masters?.hits) {
        const trackingList = result.data.searchFactFinderNg.masters.hits
          .map((hit) => hit.masterValuesObject.catalogCategoryData)
          .filter((categoryData) => categoryData);
        this.trackViewItemList(trackingList);
      }
    },
    clearAllFacets() {
      this.enableAutoFilters();
      this.filters = [];
      this.changeFacet();
    },
    changeFacet(...changes) {
      this.applyChanges(...changes);

      if (!IS_SSR) {
        let state = this.filterUrlParams;

        const page = this.$fischer.query('page');
        if (page) {
          state = state
            ? `${state}&page=${encodeURIComponent(page)}`
            : `?page=${encodeURIComponent(page)}`;
        }

        const customQuery = [];
        const query = this.$fischer.query();
        const filterKeys = ['filter', 'query'];
        Object.entries(query).forEach(([key, value]) => {
          if (!filterKeys.some((el) => key.startsWith(el))) {
            customQuery.push(`${key}=${encodeURIComponent(value)}`);
          }
        });

        if (customQuery.length) {
          if (this.filterUrlParams) {
            state = `${this.filterUrlParams}&${customQuery.join('&')}`;
          } else {
            state = `?${customQuery.join('&')}`;
          }
        }

        if (state !== String(window.location.search)) {
          // we want to remove the page parameter here, because the state changed
          window.history.replaceState('', '', state || '?');

          this.page = 1;
          this.pageNonResponsive = 1;
        }
      }

      if (this.isFite) {
        this.scrollToResults();
      }
    },
    getPaginationString(obj) {
      return this.$dict.get(
        this.isSearchPage || this.variantsWithoutFamily
          ? 'product-List.Pagination.Show-Sum'
          : 'product-List.Pagination.Show-Families',
        obj,
      );
    },
    masterClicked(record) {
      if (record?.masterValuesObject?.catalogCategoryData) {
        this.trackSelectItem(record.masterValuesObject.catalogCategoryData);
      }
    },
    mobileFacetClosed() {
      if (this.isFite) {
        this.scrollToResults();
      }
    },
    scrollToResults() {
      if (this.$refs.itemWrapper) {
        setTimeout(() => {
          this.$smoothScroll({
            scrollTo: this.$refs.itemWrapper,
            duration: 300,
            updateHistory: false,
            offset: -60,
          });
        }, 300);
      }
    },
    onIntersect(observer) {
      // if (
      //   !observer?.[0]?.isIntersecting &&
      //   observer[0]?.target?.getBoundingClientRect?.()?.top <= 0
      // ) {
      //   this.filtersOutOfView = true;
      //   this.$store.commit('product/SET_IS_FILTERS_FIXED', true);
      // } else {
      //   this.filtersOutOfView = false;
      //   this.$store.commit('product/SET_IS_FILTERS_FIXED', false);
      // }
    },
    toggleFilter() {
      this.showFilters = !this.showFilters;
      try {
        window.sessionStorage.setItem(categoryFilterExpander, this.showFilters);
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style src="./search-fact-finder-fi-unscoped" />

<style scoped src="./search-fact-finder-fi" />
